<template>
  <div class="content" :class="{ fullscreen: editLink }">
    <div class="breadcrumb">
      <button class="back-to-links" @click="backToSmartLinks()">
        <ChevronDownIcon />
        Back to Smart Links
      </button>
    </div>
    <div
      v-if="link.id"
      class="link-card"
      :class="{ none: showDateRangePickerCalendar }"
    >
      <div class="link-image">
        <div class="image-wrapper">
          <img :alt="link.title" :src="link.image" />
          <template v-if="isRelease">
            <TrackIcon class="icon extra-small" />
          </template>
          <template v-if="isBio">
            <ProfileIcon class="icon extra-small" />
          </template>
          <template v-if="isPlaylist">
            <PlaylistIcon class="icon extra-small" />
          </template>
        </div>
      </div>
      <div class="link-details">
        <div class="left">
          <div class="left-title">
            <span class="link-title">{{ link.title }}</span>
            <span class="link-artist">{{ artist.name }}</span>
          </div>
          <div class="link-url">
            <div class="url-box">
              <span class="url-text"
                >https://{{ link.domain }}/{{ link.path }}</span
              >
              <div
                @click="
                  doCopy(
                    'https://' + link.domain + '/' + link.path,
                    'Smart Link'
                  )
                "
                v-tooltip="{
                  content: `
                      <span>Copy link</span>
                  `
                }"
                class="copy"
              >
                <CopyIcon />
              </div>
              <div
                @click="openLink"
                v-tooltip="{
                  content: `
                      <span>Open link</span>
                  `
                }"
                class="external-link"
              >
                <ExternalLinkIcon />
              </div>
            </div>
          </div>
        </div>
        <div class="right">
          <button
            @click="
              doCopy(
                embedCode('https://' + link.domain + '/' + link.path),
                'Embed Code'
              )
            "
            class="button-secondary copy-embed-code"
          >
            <span>Copy embed code</span>
            <ArrowsIcon />
          </button>
          <button @click="editLink = true" class="button-default edit-page">
            <span>Edit <span class="edit-page-span">page</span></span>
            <EditIcon />
          </button>
        </div>
      </div>
    </div>

    <template v-if="!loaded">
      <div class="loading">
        <svg
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xmlns:xlink="http://www.w3.org/1999/xlink"
          x="0px"
          y="0px"
          viewBox="0 0 100 100"
          enable-background="new 0 0 0 0"
          xml:space="preserve"
        >
          <circle fill="#fff" stroke="none" cx="6" cy="50" r="6">
            <animate
              attributeName="opacity"
              dur="1s"
              values="0;1;0"
              repeatCount="indefinite"
              begin="0.1"
            />
          </circle>
          <circle fill="#fff" stroke="none" cx="26" cy="50" r="6">
            <animate
              attributeName="opacity"
              dur="1s"
              values="0;1;0"
              repeatCount="indefinite"
              begin="0.2"
            />
          </circle>
          <circle fill="#fff" stroke="none" cx="46" cy="50" r="6">
            <animate
              attributeName="opacity"
              dur="1s"
              values="0;1;0"
              repeatCount="indefinite"
              begin="0.3"
            />
          </circle>
        </svg>
      </div>
    </template>
    <div
      v-show="loaded"
      :class="{ calendarLoader: showDateRangePickerCalendar }"
    >
      <div class="chart-date-picker">
        <date-range-picker
          :dateRange="dateRange"
          :linkedCalendars="false"
          :locale-data="locale"
          :opens="isMobile ? 'center' : 'right'"
          :ranges="defaultRanges"
          @toggle="dateRangeToggled"
          @update="dateRangeUpdate"
        >
          <template v-slot:ranges>
            <div class="allRangs">
              <div
                class="rangs"
                v-for="(range, label) in defaultRanges"
                :key="label"
                :class="{ 'selected-range': selectedRange === label }"
                @click="selectRange(label)"
              >
                <div
                  :class="{
                    'wrapper-of-rang-and-purple-circle': true,
                    selected: selectedRange === label
                  }"
                >
                  <div
                    :class="{ 'purple-circle': selectedRange === label }"
                  ></div>
                  {{ label }}
                </div>
              </div>
            </div>
          </template>
        </date-range-picker>

        <div class="default-range-filters">
          <template v-for="(range, label) in defaultRanges">
            <div
              class="filter"
              v-if="range"
              :key="`filter_${range}`"
              :class="{ 'selected-range': selectedRange === label }"
              @click="selectRange(label)"
            >
              <div class="category-name">{{ label }}</div>
            </div>
          </template>
        </div>

        <div class="filters">
          <template v-for="(value, index) in sourceFilters">
            <div class="filter" v-if="value" :key="`filter_${index}`">
              <div class="category-name">{{ index | capitalise }}</div>
              <div class="value" v-if="index === 'referrer'">
                {{ value | referrerName }}
              </div>
              <div class="value" v-if="index === 'country'">
                {{ value | country }}
              </div>
              <div
                class="value"
                v-if="index !== 'referrer' && index !== 'country'"
              >
                {{ value }}
              </div>
              <button @click="removeSourceFilter(index)" class="remove">
                x
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <template>
      <MobileLoaders
        v-if="isMobileload"
        :isMobileload="isMobileload"
        @button-selected="handleButtonSelected"
        :class="{ none: showDateRangePickerCalendar }"
      />
      <div
        class="mobile-loaders-content"
        :class="{ none: showDateRangePickerCalendar }"
      >
        <div v-if="selectedLoader === 0 || !isMobileload">
          <div v-show="loaded" class="loaderHeight">
            <div class="l-container">
              <Loader
                v-if="
                  $apollo.queries.link_statistics.loading && showStatsLoader
                "
              />
              <div
                :class="{
                  blurred: $apollo.queries.link_statistics.loading
                }"
                class="loaderHeight"
              >
                <Chart
                  :key="selectedLoader"
                  :data="chartDataFormatted"
                  :metrics="chartMetrics"
                  @toggleMetric="toggleChartMetric"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="metrics-and-cards-container">
          <div class="metrics-container">
            <span>Metric</span>
            <div
              @click="$refs.source_stat.click()"
              class="select"
              style="max-width: 100px; float: right; margin-top: -14px"
            >
              <select
                ref="source_stat"
                name="select"
                v-model="sourcesStat"
                class="standard-select views-and-clicks-select"
              >
                <option value="views" selected>Views</option>
                <option value="clickthroughs">Clicks</option>
              </select>
              <ChevronDownIcon
                class="pointer-events-none svg-no-label views-and-clicks-select-svg"
              />
              <span class="focus"></span>
            </div>
          </div>
          <div v-show="loaded" class="split-cards">
            <div
              class="sources-card l-container"
              v-if="
                selectedLoader === 0 || selectedLoader === 1 || !isMobileload
              "
            >
              <Loader
                v-if="
                  $apollo.queries.link_statistics.loading && showStatsLoader
                "
              />
              <div
                :class="{ blurred: $apollo.queries.link_statistics.loading }"
              >
                <span class="card-title">Top Sources / UTM</span>
                <div class="source-tab-category">
                  <span
                    :class="{ active: sourceCategorySelected === 'referrer' }"
                    class="category"
                    @click="sourceCategorySelected = 'referrer'"
                    v-if="!sourceFilters.referrer"
                  >
                    Referrer
                  </span>
                  <span
                    :class="{ active: sourceCategorySelected === 'medium' }"
                    class="category"
                    @click="sourceCategorySelected = 'medium'"
                    v-if="!sourceFilters.medium"
                  >
                    Medium
                  </span>
                  <span
                    :class="{ active: sourceCategorySelected === 'source' }"
                    class="category"
                    @click="sourceCategorySelected = 'source'"
                    v-if="!sourceFilters.source"
                  >
                    Source
                  </span>
                  <span
                    :class="{ active: sourceCategorySelected === 'campaign' }"
                    class="category"
                    @click="sourceCategorySelected = 'campaign'"
                    v-if="!sourceFilters.campaign"
                  >
                    Campaign
                  </span>
                </div>
                <div
                  class="sources-list-container"
                  v-if="
                    !sourceFilters.referrer ||
                      !sourceFilters.medium ||
                      !sourceFilters.source ||
                      !sourceFilters.campaign
                  "
                >
                  <div
                    class="sources-list"
                    v-if="Object.keys(sourceCategorySelectedItems).length"
                  >
                    <div class="list-title">
                      <span>Name</span>
                      <span>{{ displayStatLabel }}</span>
                    </div>
                    <div class="list-items">
                      <div
                        v-for="record in sourceCategorySelectedItems"
                        :key="record.name"
                        class="list-item"
                        :style="{
                          background: getSourcesSourceGradientBackground(
                            sourceCategorySelectedItems,
                            record
                          )
                        }"
                      >
                        <div
                          class="source-icon"
                          v-if="
                            ['instagram', 'facebook'].includes(
                              record.name.toLowerCase()
                            )
                          "
                        >
                          <img
                            :src="
                              '/img/' +
                                record.name.toLowerCase().replace(' ', '-') +
                                '.png'
                            "
                            alt="Source"
                          />
                        </div>
                        <span
                          class="source-name"
                          :class="{
                            clickable: record.name !== 'Direct / None'
                          }"
                          @click="
                            applySourceFilter(
                              sourceCategorySelected,
                              record.name
                            )
                          "
                        >
                          {{ record.name }}
                        </span>
                        <span class="fans-count">{{
                          record[sourcesStat]
                        }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="no-data">
                    There's no {{ sourceCategorySelected }} data for this link
                    yet.
                  </div>
                </div>
              </div>
            </div>
            <div
              class="countries-card l-container"
              v-if="
                selectedLoader === 1 || selectedLoader === 2 || !isMobileload
              "
            >
              <Loader
                v-if="
                  $apollo.queries.link_statistics.loading && showStatsLoader
                "
              />
              <div
                :class="{ blurred: $apollo.queries.link_statistics.loading }"
              >
                <div class="card-header">
                  <span class="card-title">Countries</span>
                  <div class="wrapper-of-buttons">
                    <button
                      :class="[
                        'map-button',
                        { active: activeButtonMapOrList === 'list' }
                      ]"
                      @click="showCountyList()"
                      v-if="!sourceFilters.country"
                    >
                      <ListIconMap />
                      <span>
                        List
                      </span>
                    </button>
                    <button
                      :class="[
                        'map-button',
                        { active: activeButtonMapOrList === 'map' }
                      ]"
                      @click="showMapOn()"
                      v-if="!sourceFilters.country"
                    >
                      <MapIcon />
                      <span>
                        Map
                      </span>
                    </button>
                  </div>
                </div>
                <template v-if="loaded">
                  <AsyncWorldMap
                    class="map map-big"
                    v-show="!showCountriesList || sourceFilters.country"
                    :data="mapData"
                    :key="JSON.stringify(mapData)"
                    @onRegionClick="code => applyCountryFilter(code)"
                  />

                  <div
                    v-show="showCountriesList && !sourceFilters.country"
                    class="countries-list-container"
                  >
                    <div class="countries-list">
                      <div class="list-title">
                        <span>Country</span>
                        <span>{{ displayStatLabel }}</span>
                      </div>
                      <div class="list-items">
                        <div
                          v-for="(country, index) in sortedCountryData"
                          :key="index"
                          class="list-item"
                          :style="{
                            background: getCountriesSourceGradientBackground(
                              country
                            )
                          }"
                        >
                          <div class="country-flag">
                            <img
                              :src="'/img/flags/' + country.country + '.png'"
                              alt="Source"
                            />
                          </div>
                          <span
                            class="country-name"
                            @click="applyCountryFilter(country.country)"
                          >
                            {{ country.country | country }}
                          </span>
                          <span class="fans-count">{{ country.count }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div
      v-if="link"
      class="destination-links-card"
      :class="{ none: showDateRangePickerCalendar }"
    >
      <template v-if="isPreRelease">
        <span class="card-title">Pre-Release Services (Campaign Totals)</span>
        <div class="table-container">
          <table>
            <thead>
              <tr class="head">
                <th>
                  <div class="th-content reorder">
                    <span>Re-order</span>
                  </div>
                </th>
                <th align="left">
                  <div class="th-content">
                    <span>Destination</span>
                  </div>
                </th>
                <th align="left">
                  <div class="th-content">
                    <span>Presaves + Subscribers</span>
                  </div>
                </th>
              </tr>
            </thead>
            <draggable
              v-model="enabledDestinations"
              filter=".head"
              handle=".handle"
              tag="tbody"
              @change="destinationSort"
              @end="drag = false"
              @start="drag = true"
            >
              <tr
                v-for="item in enabledDestinations"
                :key="item.id"
                class="row"
              >
                <td class="handle">
                  <div class="handle-icon">
                    <DraghandleIcon />
                  </div>
                </td>
                <td>
                  <div class="service">
                    <div class="destination-logo">
                      <img
                        v-if="item.type !== 'custom'"
                        :src="'/img/destinations/' + item.type + '_dark.png'"
                        class="destination-image"
                      />
                      <img
                        v-else-if="item.logo"
                        :src="item.logo"
                        class="destination-image"
                      />
                    </div>
                    <div class="platform">{{ item.description }}</div>
                  </div>
                </td>
                <td
                  class="clickthroughs"
                  :class="{ itunes: item.type === 'itunes' }"
                >
                  <span v-if="item.type === 'itunes'">
                    <strong>{{ clicksCount[item.id] || 0 }}</strong>
                  </span>
                  <span v-else>
                    <strong>{{
                      link.artist.presave_subscriptions.reduce(
                        (acc, curr) =>
                          curr.presave_token.provider === item.type &&
                          curr.link_id === link.id
                            ? acc + 1
                            : acc,
                        0
                      )
                    }}</strong>
                    <strong
                      >(+{{
                        link.artist.presave_subscriptions.reduce(
                          (acc, curr) =>
                            curr.presave_token.provider === item.type &&
                            curr.link_id === null
                              ? acc + 1
                              : acc,
                          0
                        )
                      }})</strong
                    >
                  </span>
                </td>
              </tr>
            </draggable>
          </table>
        </div>
      </template>

      <template v-else>
        <span class="card-title">Destination Links</span>
        <p class="card-text">
          While <strong>{{ link.domain }}/{{ link.path }}</strong> opens your
          landing page, you can also send fans to a destination and benefit from
          pixel tracking.<br /><strong>Direct links</strong> redirect the user
          to the site or app, and may cause issues with certain ads.<br /><strong
            >Deeplinks</strong
          >
          try opening the app, and show the landing page if they don't have
          it.<br />
          <strong>We recommend using deeplinks for Facebook ads.</strong>
        </p>
        <div class="table-container-2">
          <table>
            <thead>
              <tr class="head">
                <!-- <th>
                  <div class="th-content reorder">
                    <span>Re-order</span>
                  </div>
                </th> -->
                <th align="left">
                  <div class="th-content">
                    <span>Destination</span>
                  </div>
                </th>
                <th align="left">
                  <div class="th-content">
                    <span>Clickthroughs</span>
                  </div>
                </th>
                <th align="left">
                  <div class="th-content">
                    <span>Links</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tr class="row" v-for="item in filteredDestinations" :key="item.id">
              <td class="destination-details">
                <img
                  v-if="item.type !== 'custom'"
                  :src="'/img/destinations_svg/' + destinationIconType(item.type) + '.svg'"
                  class="destination-image"
                />
                <template v-else-if="item.logo">
                  <div class="destinationLinks">
                    <img :src="item.logo" class="destination-image" />
                  </div>
                </template>
                <template>
                  <div class="destination-name">
                    {{ destination_defaults[item.type].name }}
                  </div>
                </template>
              </td>
              <td class="clickthrough-data">
                <div
                  class="clickthrough"
                  :style="{
                    background: getDestinationGradientBackground(
                      clicksCount[item.id] || 0
                    )
                  }"
                >
                  {{ clicksCount[item.id] || 0 }}
                  <span class="clickthrough-percentage">
                    ({{
                      clicksCount[item.id] || 0 | clicksPercent(totalClicks)
                    }})
                  </span>
                </div>
              </td>
              <td class="links">
                <button
                  @click="doCopy(directLinkURL(item), 'Direct Link')"
                  class="button-secondary"
                >
                  <span>Direct link</span>
                </button>
                <button
                  v-if="item.deeplink_ios"
                  @click="
                    doCopy(
                      'https://' +
                        link.domain +
                        '/' +
                        link.path +
                        '/' +
                        (duplicatePlatforms.includes(item.type) ||
                        item.type === 'custom'
                          ? item.id
                          : item.type) +
                        ':auto',
                      'Deeplink'
                    )
                  "
                  class="button-secondary deeplink-button"
                >
                  <span>Deep Link</span>
                </button>
              </td>
            </tr>
          </table>
        </div>
        <template v-if="disabledDestinations.length">
          <div class="disabled-title" :class="{ closeNone: isMobileload }">
            Disabled Links
          </div>
          <div class="table-container" :class="{ closeNone: isMobileload }">
            <table>
              <thead>
                <tr class="head">
                  <th align="left">
                    <div class="th-content">
                      <span>Destination</span>
                    </div>
                  </th>
                  <th align="left">
                    <div class="th-content">
                      <span>Direct Link</span>
                      <TooltipIcon
                        v-tooltip="{
                          content: `
                        <span>Direct links redirect the user to the site or app, and may cause issues with certain ads.</span>
                    `
                        }"
                        class="icon tiny"
                      />
                    </div>
                  </th>
                  <th align="left">
                    <div class="th-content">
                      <span>Deeplink and Fallback</span>
                      <TooltipIcon
                        v-tooltip="{
                          content: `
                        <span>Deeplinks try opening the app, and show the landing page if they don't have it.</span>
                        <span class='block bold'>We recommend using deeplinks for Facebook ads.</span>
                    `
                        }"
                        class="icon tiny"
                      />
                    </div>
                  </th>
                  <th align="left">
                    <div class="th-content">
                      <span>Clickthroughs</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in disabledDestinations"
                  :key="item.id"
                  class="row"
                >
                  <td>
                    <img
                      v-if="item.type !== 'custom'"
                      :src="'/img/destinations/' + item.type + '_dark.png'"
                      class="destination-image"
                    />
                    <img
                      v-else-if="item.logo"
                      :src="item.logo"
                      class="destination-image"
                    />
                    <template v-if="item.type === 'message_optin'"
                      >Opt-In</template
                    >
                    <template v-else>{{ item.text }}</template>
                  </td>
                  <td>
                    <button
                      @click="doCopy(directLinkURL(item), 'Direct Link')"
                      class="button-default"
                    >
                      <span>Copy Direct Link</span>
                      <CopyIcon />
                    </button>
                  </td>
                  <td class="deeplink-fallback">
                    <template v-if="item.deeplink_ios">
                      <button
                        @click="
                          doCopy(
                            'https://' +
                              link.domain +
                              '/' +
                              link.path +
                              '/' +
                              (duplicatePlatforms.includes(item.type) ||
                              item.type === 'custom'
                                ? item.id
                                : item.type) +
                              ':auto',
                            'Deeplink'
                          )
                        "
                        class="button-default"
                      >
                        <span>Copy Deeplink</span>
                        <CopyIcon />
                      </button>
                    </template>
                    <span v-else><i>Not available</i></span>
                  </td>
                  <td class="clickthroughs">
                    <span>
                      <strong>{{ item.clickthroughs }}</strong>
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>

        <div v-if="!destinations_loaded" class="loading">
          <svg
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            x="0px"
            y="0px"
            viewBox="0 0 100 100"
            enable-background="new 0 0 0 0"
            xml:space="preserve"
          >
            <circle fill="#fff" stroke="none" cx="6" cy="50" r="6">
              <animate
                attributeName="opacity"
                dur="1s"
                values="0;1;0"
                repeatCount="indefinite"
                begin="0.1"
              />
            </circle>
            <circle fill="#fff" stroke="none" cx="26" cy="50" r="6">
              <animate
                attributeName="opacity"
                dur="1s"
                values="0;1;0"
                repeatCount="indefinite"
                begin="0.2"
              />
            </circle>
            <circle fill="#fff" stroke="none" cx="46" cy="50" r="6">
              <animate
                attributeName="opacity"
                dur="1s"
                values="0;1;0"
                repeatCount="indefinite"
                begin="0.3"
              />
            </circle>
          </svg>
        </div>
      </template>
    </div>

    <div class="delete-page">
      <button @click="showPixelModal = true" class="button-secondary">
        <span>Campaign Pixel Overrides</span>
      </button>
      <button @click="deleteLink()" class="button-text danger">
        <span>Delete page</span>
        <RemoveIcon />
      </button>
    </div>

    <div v-if="showPixelModal" class="pixel-override-overlay" @click.self="showPixelModal = false">
      <div class="pixel-override-modal">
        <div class="pixel-override-header">
          <span class="modal-title">Campaign Pixel Overrides</span>
          <button class="modal-close" @click="showPixelModal = false">×</button>
        </div>
        <div class="pixel-override-body">
          <div v-if="linkPixels.length" class="pixels-table">
            <table>
              <thead>
                <tr class="head">
                  <th><div class="th-content"><span>Provider</span></div></th>
                  <th><div class="th-content"><span>Pixel</span></div></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pixel in linkPixels" :key="pixel.id" class="row">
                  <td class="provider"><span>{{ pixel.provider }}</span></td>
                  <td class="pixel"><span>{{ pixel.pixel }}</span></td>
                  <td class="actions">
                    <div class="actions-container">
                      <button @click="deleteLinkPixel(pixel)">
                        <span>Delete</span>
                        <RemoveIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="add-pixel">
            <div class="add-pixel-title">Add pixel</div>
            <div class="providers">
              <span>Providers</span>
              <div class="remaining">
                <div
                  class="provider"
                  v-for="(provider, i) in allPixelProviders"
                  :key="i"
                  :class="{ selected: provider === linkPixelProvider }"
                  @click="linkPixelProvider = provider"
                >
                  {{ provider }}
                </div>
              </div>
              <div class="inputs">
                <div class="social-input">
                  <label>{{ linkPixelProvider === 'AdWords' ? 'Tag ID' : 'Pixel ID' }}</label>
                  <div class="input-container">
                    <input v-model="linkPixelId" type="text" placeholder="Paste the pixel ID" />
                  </div>
                </div>
                <div class="social-input" v-if="linkPixelProvider !== 'AdWords'">
                  <label>API Access Token</label>
                  <div class="input-container">
                    <input v-model="linkPixelToken" type="text" placeholder="Paste the access token" />
                  </div>
                </div>
              </div>
              <div class="pixel-actions">
                <button
                  class="button-default"
                  @click="saveLinkPixel"
                  :disabled="!linkPixelId || (!linkPixelToken && linkPixelProvider !== 'AdWords') || !linkPixelProvider"
                >
                  Add Pixel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showDateRangePickerCalendar"
      class="background-overlay calendar"
      :class="{ none: showDateRangePickerCalendar }"
    ></div>
    <SnackBar>
      <template v-slot:icon>
        <CopyIcon />
      </template>
    </SnackBar>
    <EditLinkWizard
      v-if="editLink"
      :link="link"
      :destinations="link_destinations"
      @close="onEditLinkWizardCancel"
      @finish="onEditLinkWizardUpdate"
      :class="{ none: showDateRangePickerCalendar }"
    />
  </div>
</template>

<script>
// import CloseIcon from "../../../../public/img/icons/close.svg";
import ArrowsIcon from "../../../../public/img/icons/arrows.svg";
import MapIcon from "../../../../public/img/icons/map-icon.svg";
import ListIconMap from "../../../../public/img/icons/list-icon-map.svg";
import ChevronDownIcon from "../../../../public/img/icons/chevron-down.svg";
import CopyIcon from "../../../../public/img/icons/copy.svg";
import TrackIcon from "../../../../public/img/icons/categories/track.svg";
import ProfileIcon from "../../../../public/img/icons/categories/profile.svg";
import PlaylistIcon from "../../../../public/img/icons/categories/playlist.svg";
import DraghandleIcon from "../../../../public/img/icons/drag-2.svg";
import ExternalLinkIcon from "../../../../public/img/icons/external-link.svg";
import TooltipIcon from "../../../../public/img/icons/tooltip.svg";
import EditIcon from "../../../../public/img/icons/edit.svg";
import RemoveIcon from "../../../../public/img/icons/remove.svg";
import Loader from "@/components/Custom/Loader";
import draggable from "vuedraggable";
import DateRangePicker from "vue2-daterange-picker";
import Swal from "sweetalert2";
import moment from "moment";
// import { v4 as uuidv4 } from "uuid";
import AsyncWorldMap from "@/components/WorldMap/AsyncWorldMap.vue";
import Chart from "@/components/Custom/Chart.vue";
import { countryCodes } from "@/utils";
import {
  M_DELETE_LINK,
  M_UPDATE_LINK_DESTINATION_PRIORITY,
  M_FILL_MISSING_DEEPLINKS,
  Q_LINK_DESTINATIONS,
  // Q_LINK_EVENTS_COUNTRY,
  // Q_LINK_EVENTS_DAILY,
  // Q_LINK_EVENTS_REFERRER,
  // Q_LINK_EVENTS_UTM_CAMPAIGN,
  // Q_LINK_EVENTS_UTM_MEDIUM,
  // Q_LINK_EVENTS_UTM_SOURCE,
  Q_STATS,
  S_LINK,
  M_INSERT_PIXEL,
  M_DELETE_PIXEL,
  Q_LINK_PIXELS
} from "@/graphql";
import SnackBar from "@/components/Custom/SnackBar";
import EditLinkWizard from "@/components/Custom/EditLinkWizard";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";
import "@/assets/scss/md/_daterangepicker.scss";
import MobileLoaders from "@/components/Custom/MobileLoaders";

export default {
  name: "SmartLink",
  components: {
    MapIcon,
    ListIconMap,
    ChevronDownIcon,
    ArrowsIcon,
    TrackIcon,
    ProfileIcon,
    PlaylistIcon,
    CopyIcon,
    DraghandleIcon,
    EditIcon,
    ExternalLinkIcon,
    TooltipIcon,
    RemoveIcon,
    AsyncWorldMap,
    draggable,
    DateRangePicker,
    SnackBar,
    EditLinkWizard,
    Chart,
    Loader,
    MobileLoaders
    // CloseIcon,
  },
  data() {
    return {
      selectedRange: null,
      // isChartPickerActive: false,
      isMobileload: window.innerWidth <= 660,
      selectedLoader: null,
      showStatsLoader: true,
      showSourcesLoader: true,
      showCountriesLoader: true,
      isArtistSwitching: false,
      artistSwitchComplete: false,
      locale: {
        format: "mmmm d"
      },
      chartDataFormatted: {},
      chartMetrics: {},
      views_loaded: false,
      clicks_loaded: false,
      destinations_loaded: false,
      sourcesStat: "views",
      showCountriesList: true,
      showMap: false,
      activeButtonMapOrList: "list",
      showDateRangePickerCalendar: false,
      sourceCategorySelected: "referrer",
      window: window,
      deeplinksFilled: false,
      link: {},
      link_destinations: [],
      linkCreator: false,
      editLink: false,
      createLink: null,
      theme: "",
      preview: null,
      links: null,
      wizardModel: {},
      metaUrl: "",
      metaData: {},
      mapData: {},
      countriesFansSorted: [],
      countriesViewsSorted: [],
      countriesClicksSorted: [],
      sourcesReferrer: [],
      sourcesUtmMedium: [],
      sourcesUtmSource: [],
      sourcesUtmCampaign: [],
      chartData: [],
      dateRange: {
        startDate: moment()
          .subtract(1, "month")
          .toDate(),
        endDate: moment()
          .endOf("day")
          .toDate()
      },
      defaultRanges: {
        "Last 7 Days": [
          moment()
            .subtract(7, "day")
            .toDate(),
          moment().toDate()
        ],
        "Last 14 Days": [
          moment()
            .subtract(14, "day")
            .toDate(),
          moment().toDate()
        ],
        "Last 30 Days": [
          moment()
            .subtract(30, "day")
            .toDate(),
          moment().toDate()
        ],
        "Last 12 Months": [
          moment()
            .subtract(12, "month")
            .toDate(),
          moment().toDate()
        ],
        Lifetime: [
          moment()
            .startOf("year")
            .toDate(),
          moment().toDate()
        ]
      },
      //defaultRanges: {
      // "This week": [
      //   moment()
      //     .startOf("day")
      //     .toDate(),
      //   moment()
      //     .add(6, "days")
      //     .endOf("day")
      //     .toDate(),
      // ],
      // "Last week": [
      //   moment()
      //     .subtract(6, "day")
      //     .toDate(),
      //   moment().toDate(),
      // ],
      // "Last month": [
      //   moment()
      //     .subtract(1, "month")
      //     .startOf("month")
      //     .toDate(),
      //   moment()
      //     .subtract(1, "month")
      //     .endOf("month")
      //     .toDate(),
      // ],
      // "This month": [
      //   moment()
      //     .startOf("month")
      //     .toDate(),
      //   moment()
      //     .endOf("month")
      //     .toDate(),
      // ],
      // "This year": [
      //   moment()
      //     .startOf("year")
      //     .toDate(),
      //   moment()
      //     .startOf("day")
      //     .toDate(),
      // ],
      // Lifetime: [
      //   moment()
      //     .startOf("year")
      //     .toDate(),
      //   moment().toDate(),
      // ],
      //},
      showPixelModal: false,
      linkPixels: [],
      linkPixelProvider: '',
      linkPixelId: '',
      linkPixelToken: '',
      allPixelProviders: ['Facebook', 'TikTok', 'AdWords', 'Snapchat'],
      enabledDestinations: [],
      disabledDestinations: [],
      clicksCount: {},
      sourceFilters: {
        referrer: null,
        medium: null,
        source: null,
        campaign: null,
        country: null
      },
      countriesList: [],
      destination_defaults: {
        spotify: {
          name: "Spotify",
          type: "spotify",
          default_cta: "Stream on Spotify",
          text: "Stream on Spotify"
        },
        applemusic: {
          name: "Apple Music",
          type: "applemusic",
          default_cta: "Stream on Apple Music",
          text: "Stream on Apple Music"
        },
        amazonmusic: {
          name: "Amazon Music",
          type: "amazonmusic",
          default_cta: "Play on Amazon Music",
          text: "Play on Amazon Music"
        },
        deezer: {
          name: "Deezer",
          type: "deezer",
          default_cta: "Play on Deezer",
          text: "Play on Deezer"
        },
        itunes: {
          name: "iTunes",
          type: "itunes",
          default_cta: "Buy on iTunes",
          text: "Buy on iTunes"
        },
        pandora: {
          name: "Pandora",
          type: "pandora",
          default_cta: "Play on Pandora",
          text: "Play on Pandora"
        },
        tidal: {
          name: "Tidal",
          type: "tidal",
          default_cta: "Play on Tidal",
          text: "Play on Tidal"
        },
        soundcloud: {
          name: "SoundCloud",
          type: "soundcloud",
          default_cta: "Play on SoundCloud",
          text: "Play on SoundCloud"
        },
        youtube: {
          name: "YouTube",
          type: "youtube",
          default_cta: "Watch on YouTube",
          text: "Watch on YouTube"
        },
        bandcamp: {
          name: "Bandcamp",
          type: "bandcamp",
          default_cta: "Download on Bandcamp",
          text: "Download on Bandcamp"
        },
        melon: {
          name: "Melon",
          type: "melon",
          default_cta: "Play on Melon",
          text: "Play on Melon"
        },
        resso: {
          name: "Resso",
          type: "resso",
          default_cta: "Play on Resso",
          text: "Play on Resso"
        },
        applepodcasts: {
          name: "Apple Podcasts",
          type: "applepodcasts",
          default_cta: "Listen on Apple Podcasts",
          text: "Listen on Apple Podcasts"
        },
        custom: {
          name: "Custom",
          type: "custom",
          default_cta: "Custom",
          text: "Custom"
        },
        spotify_action: {
          name: "Spotify Action",
          type: "spotify_action",
          default_cta: "Save on Spotify",
          text: "Save on Spotify"
        },
        applemusic_action: {
          name: "Apple Music Action",
          type: "applemusic_action",
          default_cta: "Save on Apple Music",
          text: "Save on Apple Music"
        }
      }
    };
  },
  apollo: {
    links_by_pk: {
      client: "httpClient",
      query: S_LINK,
      variables() {
        // Use vue reactive properties here
        return {
          link_id: this.$route.params.id
        };
      },
      skip() {
        return this.isArtistSwitching && !this.artistSwitchComplete;
      },
      result({ data }) {
        this.link = data.links_by_pk;
        if (this.link)
          this.defaultRanges["Lifetime"][0] = new Date(
            new Date(this.link.created_at).setHours(0, 0, 0, 0)
          );
        if (this.views_loaded) this.updateChart();
      }
    },
    link_statistics: {
      client: "httpClient",
      query: Q_STATS,
      variables() {
        return {
          link_id: this.$route.params.id,
          start_time: this.dateRange.startDate,
          end_time: this.dateRange.endDate,
          offset: new Date().toString().match(/([-+][0-9]+)\s/)[1],
          referrer: this.sourceFilters.referrer,
          utm_campaign: this.sourceFilters.campaign,
          utm_medium: this.sourceFilters.medium,
          utm_source: this.sourceFilters.source,
          country: this.sourceFilters.country
        };
      },
      skip() {
        return this.isArtistSwitching && !this.artistSwitchComplete;
      },
      result({ data }) {
        this.chartData = [];
        let mapData = {};
        let countriesFans = [];

        this.views_loaded = false;
        if (data.link_statistics && data.link_statistics.countries.length) {
          let countries = data.link_statistics.countries;
          let countriesByViews = countries.filter(
            country => country.event === "view"
          );
          let countriesByClicks = countries.filter(
            country => country.event === "clickthrough"
          );

          for (let i = 0; i < data.link_statistics.countries.length; i++) {
            let record = data.link_statistics.countries[i];
            mapData[record.country] = parseInt(record.count);
          }

          this.mapData = mapData;
          this.countriesFansSorted = countriesFans.sort((a, b) => {
            return b.fans - a.fans;
          });
          this.countriesViewsSorted = countriesByViews.length
            ? countriesByViews.sort((a, b) => b.count - a.count)
            : [];
          this.countriesClicksSorted = countriesByClicks.length
            ? countriesByClicks.sort((a, b) => b.count - a.count)
            : [];
        } else {
          this.mapData = {};
          this.countriesFansSorted = [];
          this.countriesViewsSorted = [];
          this.countriesClicksSorted = [];
        }

        if (
          data.link_statistics.events.length ||
          data.link_statistics.platform_stats.length
        ) {
          let days = {};
          let start = moment(this.dateRange.startDate);
          let end = moment(this.dateRange.endDate);
          let diff = end.diff(start, "days") + 1;

          // loop through days and push each day to this.chartData
          for (let i = 0; i < diff; i++) {
            let day = moment(this.dateRange.startDate)
              .add(i, "days")
              .format("YYYY-MM-DD");
            if (typeof days[day] == "undefined") {
              days[day] = {
                day: day,
                views: 0,
                clickthroughs: 0,
                subscribes: 0,
                presaves: 0,
                spotify_likes: 0
              };
            }
          }

          for (const day in days) {
            this.chartData.push(days[day]);
          }

          for (let i = 0; i < data.link_statistics.events.length; i++) {
            let record = data.link_statistics.events[i];
            record.count = parseInt(record.count);

            let ds = record.day.slice(0, 10);
            if (typeof days[ds] == "undefined") continue;
            days[ds][record.event + "s"] = record.count;
          }

          for (let i = 0; i < data.link_statistics.platform_stats.length; i++) {
            let record = data.link_statistics.platform_stats[i];
            record.count = parseInt(record.value);

            let ds = record.day.slice(0, 10);
            if (typeof days[ds] == "undefined") continue;
            days[ds][`${record.platform}_${record.metric}`] = record.count;
          }
        }

        if (data.link_statistics.campaigns.length) {
          let referrers = {};
          let campaigns = {};
          let mediums = {};
          let sources = {};

          for (let i = 0; i < data.link_statistics.campaigns.length; i++) {
            let record = data.link_statistics.campaigns[i];
            record.count = parseInt(record.count);

            if (record.event !== "view" && record.event !== "clickthrough") {
              continue;
            }

            if (record.referrer === null) record.referrer = "Direct / None";
            if (record.utm_campaign === null)
              record.utm_campaign = "Direct / None";
            if (record.utm_medium === null) record.utm_medium = "Direct / None";
            if (record.utm_source === null) record.utm_source = "Direct / None";

            if (record.referrer) {
              /* Standardization */
              if (record.referrer.endsWith("facebook.com")) {
                record.referrer = "Facebook";
              } else if (record.referrer.endsWith("instagram.com")) {
                record.referrer = "Instagram";
              } else if (record.referrer.endsWith("youtube.com")) {
                record.referrer = "YouTube";
              } else if (record.referrer.endsWith("tiktok.com")) {
                record.referrer = "TikTok";
              } else if (record.referrer.endsWith("google.com")) {
                record.referrer = "Google";
              } else if (record.referrer.endsWith("t.co")) {
                record.referrer = "X (Twitter)";
              }

              if (typeof referrers[record.referrer] == "undefined") {
                referrers[record.referrer] = {
                  name: record.referrer,
                  views: 0,
                  clickthroughs: 0
                };
              }
              referrers[record.referrer][record.event + "s"] += record.count;
            }

            if (record.utm_campaign) {
              if (typeof campaigns[record.utm_campaign] == "undefined") {
                campaigns[record.utm_campaign] = {
                  name: record.utm_campaign,
                  views: 0,
                  clickthroughs: 0
                };
              }
              campaigns[record.utm_campaign][record.event + "s"] +=
                record.count;
            }

            if (record.utm_medium) {
              if (typeof mediums[record.utm_medium] == "undefined") {
                mediums[record.utm_medium] = {
                  name: record.utm_medium,
                  views: 0,
                  clickthroughs: 0
                };
              }
              mediums[record.utm_medium][record.event + "s"] += record.count;
            }

            if (record.utm_source) {
              if (typeof sources[record.utm_source] == "undefined") {
                sources[record.utm_source] = {
                  name: record.utm_source,
                  views: 0,
                  clickthroughs: 0
                };
              }
              sources[record.utm_source][record.event + "s"] += record.count;
            }
          }

          this.sourcesReferrer = referrers;
          this.sourcesUtmCampaign = campaigns;
          this.sourcesUtmMedium = mediums;
          this.sourcesUtmSource = sources;
        }

        if (data.link_statistics.destinations.length) {
          let clicksCount = {};

          data.link_statistics.destinations.forEach(
            ({ destination_id, count }) => {
              clicksCount[destination_id] = count;
            }
          );

          this.clicksCount = clicksCount;
        }

        this.views_loaded = true;
        this.clicks_loaded = true;

        if (this.link.id) this.updateChart();
      }
    },
    link_destinations: {
      client: "httpClient",
      query: Q_LINK_DESTINATIONS,
      variables() {
        // Use vue reactive properties here
        return {
          link_id: this.$route.params.id
        };
      },
      skip() {
        return (
          !this.link?.id ||
          (this.isArtistSwitching && !this.artistSwitchComplete)
        );
      },
      result({ data }) {
        this.link_destinations = data.link_destinations.sort((a, b) => {
          return a.priority - b.priority;
        });

        this.enabledDestinations = this.link_destinations.flatMap(el => {
          const applicable =
            el.enabled &&
            (this.isPreRelease ? el.pre_release : !el.pre_release);
          if (!applicable) return [];
          return [
            {
              ...el,
              count:
                el.presaves_aggregate.aggregate.count ||
                el.optins_aggregate.aggregate.count
            }
          ];
        });

        this.disabledDestinations = this.link_destinations.filter(
          ({ enabled, pre_release }) =>
            !enabled &&
            (this.link.type === "pre-release" ? pre_release : !pre_release)
        );

        this.destinations_loaded = true;

        this.checkAndFillMissingDeeplinks();

        const { action } = this.$route.query;
        if (action === "edit") {
          this.editLink = true;
          const query = Object.assign({}, this.$route.query);
          delete query.action;
          this.$router.replace({ query });
        }
      }
    },
    link_pixels: {
      client: "httpClient",
      query: Q_LINK_PIXELS,
      variables() {
        return { link_id: this.$route.params.id };
      },
      result({ data }) {
        this.linkPixels = data.pixels;
      }
    }
  },
  async created() {
    const artistId = this.$route.query.artist;

    if (artistId) {
      console.log("artistId", artistId);
      if (!this.artist || artistId !== this.artist.id.toString()) {
        this.isArtistSwitching = true;
        try {
          const targetArtist = this.user?.artists?.find(
            artist => artist.id.toString() === artistId
          );

          if (targetArtist) {
            await this.$store.dispatch("setArtist", targetArtist);
            this.artistSwitchComplete = true;
          } else {
            console.error("Artist not found in user's artists:", artistId);
            this.isArtistSwitching = false;
            this.artistSwitchComplete = true;
          }
        } catch (error) {
          console.error("Failed to switch artist:", error);
          this.isArtistSwitching = false;
          this.artistSwitchComplete = true;
        }
      } else {
        this.artistSwitchComplete = true;
      }
    } else {
      this.artistSwitchComplete = true;
    }

    if (this.artist && !this.subscriptionActive(this.artist)) {
      // TODO: Re-enable after fixing subs
      // await this.$router.push({ name: "ArtistSettings" });
    }

    this.$store.commit("resetCreateLinkWizardData");
    this.$store.commit("resetEditLinkWizardData");
  },
  computed: {
    filteredDestinations() {
      return this.enabledDestinations.filter(
        item => item.type !== "message_optin" && item.type !== "header"
      );
    },
    artist() {
      return this.$store.getters.getArtist;
    },
    user() {
      return this.$store.getters.getUser;
    },
    isRelease() {
      return !this.link.type || this.link.type === "release";
    },
    isBio() {
      return this.link.type === "bio";
    },
    isPlaylist() {
      return this.link.type === "playlist";
    },
    isPreRelease() {
      return this.link.type === "pre-release";
    },
    duplicatePlatforms() {
      const destinations = [
        ...this.enabledDestinations,
        ...this.disabledDestinations
      ];

      return destinations
        .map(destination => destination.type)
        .filter(
          (destination, index, self) => self.indexOf(destination) !== index
        );
    },
    sourceCategorySelectedItems() {
      let sourceData;

      if (this.sourceCategorySelected === "referrer") {
        sourceData = Object.values(this.sourcesReferrer);
      } else if (this.sourceCategorySelected === "medium") {
        sourceData = Object.values(this.sourcesUtmMedium);
      } else if (this.sourceCategorySelected === "source") {
        sourceData = Object.values(this.sourcesUtmSource);
      } else if (this.sourceCategorySelected === "campaign") {
        sourceData = Object.values(this.sourcesUtmCampaign);
      } else {
        sourceData = [];
      }

      return sourceData
        .filter(item => item[this.sourcesStat] !== 0)
        .sort((a, b) => {
          return b[this.sourcesStat] - a[this.sourcesStat];
        });
    },
    topSourcesTotal() {
      let total = 0;
      this.sourceCategorySelectedItems.forEach(x => {
        total = total + x[this.sourcesStat];
      });
      return total;
    },
    isMobile() {
      const toMatch = [
        /Android/i,
        /iPhone/i,
        /iPad/i,
        /iPod/i,
        /BlackBerry/i,
        /Windows Phone/i
      ];

      return toMatch.some(toMatchItem => {
        return navigator.userAgent.match(toMatchItem);
      });
    },
    // countriesFansSorted() {
    //   if (this.link_events_country) {
    //     return [...this.link_events_country].sort((a, b) => {
    //       return b.count - a.count;
    //     });
    //   } else {
    //     return [];
    //   }
    // },
    loaded() {
      return this.views_loaded && this.clicks_loaded;
    },
    displayStatLabel() {
      return this.sourcesStat === "clickthroughs" ? "Clicks" : "Views";
    },
    sortedCountryData() {
      if (this.sourcesStat === "clickthroughs") {
        return this.countriesClicksSorted;
      } else {
        return this.countriesViewsSorted;
      }
    },
    totalClicks() {
      return this.chartData.reduce((acc, curr) => acc + curr.clickthroughs, 0);
    }
  },
  watch: {
    selectedLoader(newLoader) {
      if (newLoader === 0 || !this.isMobileload) {
        this.$nextTick(() => {
          this.updateChart();
        });
      }
    },
    // whenever question changes, this function will run
    artist: function(newArtist, oldArtist) {
      console.log({ newArtist, oldArtist });
      if (newArtist && oldArtist) {
        if (newArtist.id !== oldArtist.id) {
          if (!this.isArtistSwitching) {
            this.$router.push({ name: "SmartLinks" });
          } else {
            this.isArtistSwitching = false;
          }
        }

        const query = Object.assign({}, this.$route.query);
        delete query.artist;

        const currentQuery = this.$route.query;
        const queryChanged =
          Object.keys(query).length !== Object.keys(currentQuery).length ||
          Object.keys(query).some(key => query[key] !== currentQuery[key]);

        if (queryChanged) {
          this.$router.replace({ query });
        }
      }
    },
    link: function(newLink, oldLink) {
      if (newLink && oldLink && newLink.id !== oldLink.id) {
        this.deeplinksFilled = false;
      }
    },
    loaded: function(newVal, oldVal) {
      console.log({ newVal, oldVal });
    },
    artistSwitchComplete: function(newVal) {
      if (newVal && this.isArtistSwitching) {
        this.$nextTick(() => {
          this.$apollo.queries.links_by_pk.refetch();
          this.$apollo.queries.link_statistics.refetch();
          this.$apollo.queries.link_destinations.refetch();
        });
      }
    }
  },
  filters: {
    country(code) {
      return countryCodes[code];
    },
    capitalise(value) {
      if (!value) return "";
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    referrerName(value) {
      if (value.endsWith("facebook.com")) {
        return "Facebook";
      } else if (value.endsWith("instagram.com")) {
        return "Instagram";
      } else if (value.endsWith("youtube.com")) {
        return "YouTube";
      } else {
        return value;
      }
    },
    clicksPercent(clicks, total) {
      return `${((clicks / total) * 100).toFixed(0)}%`;
    }
  },
  methods: {
    destinationIconType(type) {
      if (type === 'spotify_action') return 'spotify';
      if (type === 'applemusic_action') return 'applemusic';
      return type;
    },
    formatNameFromUrl(url) {
      if (!url) return "";

      const name = url
        .split("/")
        .pop()
        .split(".")[0];
      return name.charAt(0).toUpperCase() + name.slice(1);
    },
    selectRange(rangeLabel) {
      this.selectedRange = rangeLabel;

      // Update dateRange based on the selected range
      const range = this.defaultRanges[rangeLabel];
      if (range) {
        this.dateRange = {
          startDate: range[0],
          endDate: range[1]
        };
      }
    },
    // toggleChartPicker() {
    //   this.isChartPickerActive = !this.isChartPickerActive;
    //   console.log('isChartPickerActive:', this.isChartPickerActive);
    // },
    handleButtonSelected(index) {
      this.selectedLoader = index;
    },
    updateScreenSize() {
      this.isMobileload = window.innerWidth <= 660;
    },
    encodeBase62(id) {
      const characters =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
      const base = characters.length;
      let result = "";

      while (id > 0) {
        result = characters[id % base] + result;
        id = Math.floor(id / base);
      }

      return result;
    },
    directLinkURL(destination) {
      if (
        this.link_destinations.filter(dest => dest.type === destination.type)
          .length === 1 &&
        destination.type !== "custom"
      ) {
        return `https://${this.link.domain}/${this.link.path}/${destination.type}`;
      } else {
        return `https://vyb.to/${this.encodeBase62(destination.short_id)}`;
      }
    },
    rangeSelected(range, dateRange) {
      return (
        moment(range[0]).isSame(dateRange.startDate) &&
        moment(range[1]).isSame(dateRange.endDate)
      );
    },
    backToSmartLinks() {
      this.$router.push({ name: "SmartLinks" });
    },
    showCountyList() {
      this.showCountriesList = true;
      this.showMap = false;
      this.activeButtonMapOrList = "list";
    },
    showMapOn() {
      this.showCountriesList = false;
      this.showMap = true;
      this.activeButtonMapOrList = "map";
      this.$nextTick(() => {
        window.dispatchEvent(new Event("resize"));
      });
    },
    dateRangeToggled(show) {
      this.showDateRangePickerCalendar = show;
    },
    getSourcesSourcePercent(sources, source) {
      const total = this.topSourcesTotal;
      // console.log({ source, sources, total, percent });
      return ((source[this.sourcesStat] / total) * 100).toFixed(0);
    },
    getSourcesSourceGradientBackground(sources, source) {
      const percent = this.getSourcesSourcePercent(sources, source);
      const bgType1 = "#1B193C";
      return `linear-gradient(90deg, rgba(156, 39, 176, 0.2) ${percent}%, ${bgType1} ${percent}%)`;
    },
    getCountriesSourcePercent(country) {
      const total = this.topSourcesTotal;
      // console.log({ source, sources, total, percent });
      return ((country.count / total) * 100).toFixed(0);
    },
    getCountriesSourceGradientBackground(country) {
      const percent = this.getCountriesSourcePercent(country);
      const bgType1 = "#1B193C";
      return `linear-gradient(90deg, rgba(156, 39, 176, 0.2) ${percent}%, ${bgType1} ${percent}%)`;
    },
    getDestinationGradientBackground(clicks) {
      const percent = (clicks / this.totalClicks) * 100;
      const bgType1 = "#1B193C";
      return `linear-gradient(90deg, rgba(156, 39, 176, 0.2) ${percent}%, ${bgType1} ${percent}%)`;
    },
    doCopy(text, type) {
      const vm = this;
      this.$copyText(text).then(
        function(e) {
          console.log(e);
          if (type) {
            vm.$store.dispatch("snack", {
              text: `${type} copied to clipboard`
            });
          }
        },
        function(e) {
          console.log("Can not copy", text, type, e);
        }
      );
    },
    openLink() {
      const link = `https://${this.link.domain}/${this.link.path}`;
      window.open(link, "_blank");
    },
    embedCode(link) {
      return (
        '<iframe src="' +
        link +
        '" allowfullscreen style="border: 0; border-radius: 3px; height: 920px; width: 500px"></iframe>'
      );
    },
    destinationSort() {
      let priority = 1;
      for (const destination of Object.values([
        ...this.enabledDestinations,
        ...this.disabledDestinations
      ])) {
        this.$apollo.mutate({
          mutation: M_UPDATE_LINK_DESTINATION_PRIORITY,
          variables: {
            id: destination.id,
            priority: priority
          }
        });
        priority++;
      }
    },
    async checkAndFillMissingDeeplinks() {
      if (this.isPreRelease || this.deeplinksFilled || !this.link.id) {
        return;
      }

      const supportedPlatforms = [
        "spotify",
        "applemusic",
        "deezer",
        "itunes",
        "amazonmusic",
        "youtube",
        "tidal",
        "pandora"
      ];

      const hasMissingDeeplinks = this.link_destinations.some(destination => {
        if (destination.pre_release) return false;
        if (!supportedPlatforms.includes(destination.type)) return false;

        return (
          !destination.deeplink_ios || destination.deeplink_ios.trim() === ""
        );
      });

      if (hasMissingDeeplinks) {
        try {
          await this.$apollo.mutate({
            mutation: M_FILL_MISSING_DEEPLINKS,
            variables: {
              link_id: this.link.id
            }
          });

          await this.$apollo.provider.clients.httpClient.query({
            query: Q_LINK_DESTINATIONS,
            variables: { link_id: this.link.id },
            fetchPolicy: "network-only"
          });

          this.deeplinksFilled = true;
        } catch (error) {
          this.deeplinksFilled = true;
        }
      }
    },
    validateStep(ref) {
      return this.$refs[ref].validate();
    },
    onStepValidated(validated, model) {
      this.wizardModel = { ...this.wizardModel, ...model };
    },
    onEditLinkWizardCancel() {
      console.log("EditLinkWizard Cancelled");
      this.editLink = false;
      this.$store.commit("resetEditLinkWizardData");
    },
    async onEditLinkWizardUpdate() {
      console.log("EditLinkWizard Updated");
      await this.$apollo.queries.link_destinations.refetch();
      await this.$apollo.queries.links_by_pk.refetch();
      this.editLink = false;
      this.$store.commit("resetEditLinkWizardData");
      await this.$store.dispatch("snack", {
        text: "Your smart link has been updated!"
      });
    },
    openEditor: function() {
      this.editLink = this.link;
      this.$store.commit("resetEditLinkWizardData");
    },
    closeEditor: function() {
      this.editLink = null;
    },
    dateRangeUpdate: function(range) {
      this.dateRange = range;
      const tzoffset = new Date().getTimezoneOffset() * 60000;

      for (const [label, defaultRange] of Object.entries(this.defaultRanges)) {
        if (
          new Date(range.startDate.getTime() - tzoffset)
            .toISOString()
            .split("T")[0] ===
            new Date(defaultRange[0].getTime() - tzoffset)
              .toISOString()
              .split("T")[0] &&
          new Date(range.endDate.getTime() - tzoffset)
            .toISOString()
            .split("T")[0] ===
            new Date(defaultRange[1].getTime() - tzoffset)
              .toISOString()
              .split("T")[0]
        ) {
          this.selectedRange = label;
          return;
        }
      }

      this.selectedRange = null;
    },
    async deleteLink() {
      const confirmDelete = await Swal.fire({
        title: "Delete this link?",
        html:
          '<p class="description">Your link, page, and statistics will be permanently deleted.</p>',
        type: "warning",
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: "<span>Yes, delete it</span>",
        cancelButtonText: "<span>No, keep it</span>",
        confirmButtonClass: "button-danger big",
        cancelButtonClass: "button-secondary big",
        buttonsStyling: false
      });

      if (confirmDelete.value) {
        try {
          await this.$apollo.mutate({
            mutation: M_DELETE_LINK,
            variables: {
              link_id: this.link.id
            }
          });
          await this.$store.dispatch("snack", {
            text: "Success! Your link has been deleted."
          });
          await this.$router.push({ name: "SmartLinks" });
        } catch (err) {
          console.log(err);
          await this.$store.dispatch("snack", {
            type: "error",
            text: `Oops! ${err.message.replace("GraphQL error: ", "")}`
          });
        }
      } else {
        await this.$store.dispatch("snack", {
          text: "Cancelled! Your link is safe."
        });
      }
    },
    updateChart() {
      const chartMetrics = {
        Views: {
          definition: "The total number of views on the website.",
          color: "#9A2CCA"
        },
        Clicks: {
          definition: "The total number of clicks on the website's links.",
          color: "#A6CEE3"
        },
        Subscribers: {
          definition: "The total number of new subscribers to the website.",
          color: "#FFFF00"
        }
      };

      const chartDataFormatted = {
        Views: this.chartData.map(x => {
          return { date: x.day, value: x.views };
        }),
        Clicks: this.chartData.map(x => {
          return { date: x.day, value: x.clickthroughs };
        }),
        Subscribers: this.chartData.map(x => {
          return { date: x.day, value: x.subscribes };
        })
      };

      if (this.isPreRelease) {
        chartMetrics.Presaves = {
          definition: "The total number of presaves on the website.",
          color: "#1F77B4"
        };

        chartDataFormatted.Presaves = this.chartData.map(x => {
          return { date: x.day, value: x.presaves };
        });
      }

      if (this.isPlaylist) {
        chartMetrics["Spotify Likes"] = {
          definition: "The total number of Spotify likes.",
          color: "#FF7F0E",
          canBeToggled: true,
          active: false
        };

        let current =
          this.chartData.find(x => x.spotify_likes !== 0)?.spotify_likes || 0;

        chartDataFormatted["Spotify Likes"] = this.chartData.flatMap(x => {
          current = x.spotify_likes || current;
          return [{ date: x.day, value: current }];
        });
      }

      this.chartMetrics = chartMetrics;
      this.chartDataFormatted = chartDataFormatted;
    },
    toggleChartMetric(metric) {
      this.chartMetrics[metric].active = !this.chartMetrics[metric].active;
    },
    applySourceFilter(filter, value) {
      if (value === "Direct / None") return;

      this.showCountriesList = false;
      if (this.sourceCategorySelected === "referrer") {
        if (value === "Facebook") {
          this.sourceFilters.referrer = "facebook.com";
        } else if (value === "Instagram") {
          this.sourceFilters.referrer = "instagram.com";
        } else if (value === "YouTube") {
          this.sourceFilters.referrer = "youtube.com";
        } else {
          this.sourceFilters.referrer = value;
        }
      } else {
        this.sourceFilters[filter] = value;
      }

      if (!this.sourceFilters.referrer)
        this.sourceCategorySelected = "referrer";
      else if (!this.sourceFilters.medium)
        this.sourceCategorySelected = "medium";
      else if (!this.sourceFilters.source)
        this.sourceCategorySelected = "source";
      else if (!this.sourceFilters.campaign)
        this.sourceCategorySelected = "campaign";

      this.$apollo.queries.link_statistics.refetch();
    },
    removeSourceFilter(filter) {
      this.showCountriesList = false;
      this.sourceFilters[filter] = null;
      if (filter === "date") {
        this.sourceFilters[filter] = "";
        this.dateRange = {
          startDate: moment()
            .subtract(1, "month")
            .toDate(),
          endDate: moment()
            .endOf("day")
            .toDate()
        };
      }
      if (!this.sourceFilters.referrer)
        this.sourceCategorySelected = "referrer";
      else if (!this.sourceFilters.medium)
        this.sourceCategorySelected = "medium";
      else if (!this.sourceFilters.source)
        this.sourceCategorySelected = "source";
      else if (!this.sourceFilters.campaign)
        this.sourceCategorySelected = "campaign";
    },
    applyCountryFilter(country) {
      this.showCountriesList = false;
      this.sourceFilters.country = country;
      this.$apollo.queries.link_statistics.refetch();
    },
    async saveLinkPixel() {
      try {
        await this.$apollo.mutate({
          mutation: M_INSERT_PIXEL,
          variables: {
            pixel: {
              provider: this.linkPixelProvider,
              pixel: this.linkPixelId,
              access_token: this.linkPixelToken || null,
              link_id: this.$route.params.id,
              conversion_tag: this.linkPixelProvider === 'AdWords' ? this.linkPixelId : null
            }
          }
        });
        this.linkPixelId = '';
        this.linkPixelToken = '';
        this.linkPixelProvider = '';
        await this.$apollo.queries.link_pixels.refetch();
        await this.$store.dispatch('snack', { text: 'Pixel added!' });
      } catch (err) {
        await this.$store.dispatch('snack', { type: 'error', text: `Oops! ${err.message.replace('GraphQL error: ', '')}` });
      }
    },
    async deleteLinkPixel(pixel) {
      const confirmDelete = await Swal.fire({
        title: 'Delete this pixel?',
        type: 'warning',
        showCloseButton: true,
        showCancelButton: true,
        confirmButtonText: 'Yes, Delete Pixel',
        cancelButtonText: 'No, keep it',
        confirmButtonClass: 'button-danger big',
        cancelButtonClass: 'button-secondary big',
        buttonsStyling: false
      });
      if (!confirmDelete.value) return;
      try {
        await this.$apollo.mutate({ mutation: M_DELETE_PIXEL, variables: { id: pixel.id } });
        this.linkPixels = this.linkPixels.filter(p => p.id !== pixel.id);
        await this.$store.dispatch('snack', { text: 'Pixel deleted.' });
      } catch (err) {
        await this.$store.dispatch('snack', { type: 'error', text: `Oops! ${err.message}` });
      }
    }
  },
  forceUpdateChart() {
    this.updateChart();
    this.$refs.chart.updateSeries(this.series);
    this.$refs.chart.updateOptions(this.chartOptions);
  },
  mounted() {
    this.updateScreenSize();
    window.addEventListener("resize", this.updateScreenSize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScreenSize);
  }
};
</script>
<style>
.left-title {
  display: flex;
  flex-direction: column;
}
.svg-no-label {
  top: 14px !important;
}

#worldMap {
  width: 100%;
  overflow: hidden;
}

.jvectormap-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.jvectormap-container,
.jvectormap-container svg {
  -ms-touch-action: none;
  touch-action: none;
}

.jvectormap-zoomin {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(46.19deg, #9c27b0 5.66%, #9733ee 94.35%);
  border-radius: 5px;
  color: white;
  position: absolute;
  padding: 3px 6px;
  top: 4px;
  left: 4px;
}

.jvectormap-zoomout {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(46.19deg, #9c27b0 5.66%, #9733ee 94.35%);
  border-radius: 5px;
  color: white;
  position: absolute;
  padding: 3px 6px;
  top: 34px;
  left: 4px;
}

.jvectormap-tip {
  display: none;
  position: absolute;
  z-index: 1040;
  /* Gradient/type2 */
  background: linear-gradient(46.19deg, #9c27b0 5.66%, #9733ee 94.35%);
  /* Shadow-button */
  border-radius: 5px;
  border: none;
  padding: 4px 16px;
  color: white;
  /* 14/SemiBold */
  font-family: Montserrat;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  /* White */
  color: white;
}

/*.jvectormap-tip {*/
/*  display: none;*/
/*  position: absolute;*/
/*  !*display: flex;*!*/
/*  !*align-items: center;*!*/
/*  !* Gradient/type2 *!*/
/*  background: linear-gradient(46.19deg, #9c27b0 5.66%, #9733ee 94.35%);*/
/*  !* Shadow-button *!*/
/*  !*border-radius: 5px;*!*/
/*  border: none;*/
/*  padding: 4px 16px;*/
/*  color: white;*/
/*  border-radius: 30px;*/
/*  !*-webkit-box-shadow: 0 8px 10px 1px rgb(0 0 0 / 14%),*!*/
/*  !*  0 3px 14px 2px rgb(0 0 0 / 12%), 0 5px 5px -3px rgb(0 0 0 / 20%);*!*/
/*  !*box-shadow: 0 8px 10px 1px rgb(0 0 0 / 14%), 0 3px 14px 2px rgb(0 0 0 / 12%),*!*/
/*  !*  0 5px 5px -3px rgb(0 0 0 / 20%);*!*/
/*  !*padding: 5px 10px;*!*/
/*  z-index: 1040;*/
/*}*/
</style>

<style lang="scss">
.l-container {
  position: relative;

  .loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    &::before {
      width: 3em;
      height: 3em;
      border: 0.2em solid #ffffff;
      border-top-color: transparent;
    }
  }

  .blurred {
    filter: blur(5px);
  }
}
</style>

<style lang="scss" scoped>
.destinationLinks {
  display: flex;
  align-items: center;
  gap: 5px;
}
.content {
  display: flex;
  flex-direction: column;
  padding: 36px 60px;

  &.fullscreen {
    height: 100vh;
    overflow-y: hidden;
  }
}

.back-to-links {
  cursor: pointer;
  display: flex;
  align-items: center;
  //max-width: fit-content;
  border: none;
  background: $bg-type-2;
  border-radius: 5px;
  padding: 6px;
  /* 12/SemiBold */
  font-weight: 600;
  font-size: 12px;
  line-height: 15px;
  /* Text/type0 */
  color: $text-type-0;

  &:hover {
    color: white;
    background: linear-gradient(46.19deg, #9c27b0 5.66%, #9733ee 94.35%);
  }

  svg {
    margin-right: 10px;
    transform: rotate(90deg);
    width: 14px;

    path {
      stroke: white;
    }
  }
}

.link-card {
  display: flex;
  margin-top: 20px;
  //padding: 18px;
  /* Shadow */
  //box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;
  transition: all 0.3s ease-in-out;

  /* &:hover {
    transform: scale(1.005);
    border: 1px solid white;
  } */

  .link-image {
    width: 144px;
    height: 144px;
    min-width: 144px;
    box-shadow: none;

    img {
      border-radius: 10px;
      width: 144px;
      height: 144px;
      object-fit: cover;
    }

    svg {
      width: 20px;
      position: absolute;
      top: 13px;
      left: 13px;

      path {
        fill: white;
      }
    }

    .image-wrapper {
      position: relative;
      /* Shadow-button */
      //filter: drop-shadow(0px 0px 40px rgba(23, 18, 43, 0.45));
    }
  }

  .link-details {
    display: flex;
    flex: 1;

    .left {
      flex: 1;
      margin-left: 18px;
      display: flex;
      flex-direction: column;

      .link-title {
        /* 30/Bold */
        font-weight: bold;
        font-size: 30px;
        line-height: 37px;
        color: white;
        mix-blend-mode: normal;
        opacity: 0.9;
      }

      .link-artist {
        margin-top: 6px;
        /* 16/Regular */
        font-size: 16px;
        line-height: 24px;
        /* Text/type2 */
        color: $text-type-2;
      }

      .link-url {
        margin-top: 30px;
        display: flex;

        .url-box {
          display: flex;
          align-items: center;
          background: $bg-type-2;
          border-radius: 5px;
          padding: 8px 16px;

          .url-text {
            display: inline-block;
            overflow: hidden;
            text-overflow: ellipsis;
            /* 18/Regular */
            font-size: 18px;
            line-height: 26px;
            /* Text/type0 */
            color: $text-type-0;
          }

          .copy,
          .external-link {
            display: flex;

            svg {
              cursor: pointer;
              width: 24px;
              margin-left: 16px;

              &:hover {
                path {
                  fill: white;
                }
              }

              path {
                fill: $text-type-0;
              }
            }
          }
        }
      }
    }

    .right {
      display: flex;
      flex-direction: column;
      align-items: flex-end;

      .copy-embed-code {
        margin-top: 36px;
        span {
          text-wrap: nowrap;
        }
        svg {
          width: 18px;
          margin-left: 8px;

          //height: 18px;
          path {
            fill: white;
          }
        }
      }

      .edit-page {
        // margin: 0px -20px;
        display: flex;
        align-items: center;
        margin-top: 18px;
        z-index: 101;

        svg {
          width: 18px;
          margin-left: 8px;

          //height: 18px;
          path {
            fill: white;
          }
        }
      }
    }
  }
}

.chart-card,
.sources-card,
.countries-card,
.destination-links-card {
  margin-top: 36px;
  /* BG/type2 */
  background: $bg-type-2;
  /* Shadow */
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);
  border-radius: 10px;
  padding: 24px;

  .card-title {
    /* 18/SemiBold */
    font-weight: 600;
    font-size: 18px;
    line-height: 30px;
    /* White */
    color: white;
    mix-blend-mode: normal;
    opacity: 0.9;
  }
}
.chart-date-picker {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin: 25px 0;
  gap: 10px;
  padding: 10px 10px 10px 0;
  z-index: 100;
  position: sticky;
  position: -webkit-sticky;
  top: 0;
  // backdrop-filter: blur(10px);
  .vue-daterange-picker {
    ::v-deep {
      .reportrange-text {
        span {
          font-weight: 400;
          font-size: 14px;
        }
        // padding: 10px 15px;
      }
    }
  }
  select {
    margin-top: 0;
    height: 38px;
    padding: 7px 11px;
    width: 180px !important;
  }
  .svg-no-label {
    top: 5px !important;
  }
}

.chart-card {
  margin-top: 18px;

  .card-top {
    display: flex;
    justify-content: space-between;

    .summary-stats {
      display: flex;

      .stat {
        // display: flex;
        // flex-direction: column;
        // align-items: center;
        // justify-content: center;
        // min-width: 86px;
        // min-height: 40px;
        // margin-right: 24px;
        // padding: 10px 17px;
        // /* BG/type1 */
        // background: $bg-type-1;
        // border-radius: 10px;

        .title {
          /* 16/SemiBold */
          font-weight: 600;
          font-size: 16px;
          line-height: 24px;
          text-align: center;
          /* White */
          color: white;
        }

        .category {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;

          .category-color {
            width: 6px;
            height: 6px;
            border-radius: 50%;
          }

          &.views {
            .category-color {
              background: #9a2cca;
            }
          }

          &.presaves {
            .category-color {
              background: #1f78b4;
            }
          }

          &.subscribes {
            .category-color {
              background: #ffff00;
            }
          }

          &.clickthroughs {
            .category-color {
              background: #a6cee3;
            }
          }

          .category-text {
            /* 12/Regular */
            font-size: 12px;
            line-height: 15px;
            /* Text/type1 */
            color: $text-type-1;
          }
        }
      }
    }
  }

  .chart {
    width: 100%;
    height: 300px;
    margin-top: 36px;
  }
}
.metrics-and-cards-container {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.metrics-container {
  display: flex;
  margin-top: 36px;
  justify-content: flex-end;
  align-items: center;
  gap: 14px;
  span {
    color: white;
    font-size: 14px;
    font-weight: 700;
  }
}
.views-and-clicks-select {
  display: flex;
  gap: 8px;
  align-items: center;
  border-radius: 8px;
  background-color: $bg-type-3;
  padding: 8px;
  font-weight: bold;
  color: white;
  margin-top: 10px !important;
  z-index: 0 !important;
}
.views-and-clicks-select-svg {
  background-color: $bg-type-3 !important;
  width: 25px !important;
  z-index: -20px !important;
}
.split-cards {
  display: flex;
  grid-gap: 30px;

  .sources-card,
  .countries-card {
    width: 50%;
    .source-tab-category {
      display: flex;
      margin-top: 16px;
      gap: 14px;
      .category {
        cursor: pointer;
        /* 16/SemiBold */
        font-weight: 400;
        font-size: 15px;
        line-height: 24px;
        /* Text/type1 */
        color: $text-type-2;
        border-radius: 20px;
        padding: 4px 8px;

        &.active {
          /* 16/SemiBold */
          font-size: 16px;
          line-height: 24px;
          color: $bg-type-1;
          background-color: #ffffff;
        }
      }
    }

    .no-data {
      padding: 24px 9px;
    }

    .sources-list {
      //overflow: hidden;
      margin-top: 15px;
      height: 300px;

      .list-title {
        display: flex;
        justify-content: space-between;
        padding: 12px 8px;
        margin-right: 9px;
        /* 10/SemiBold */
        font-weight: 600;
        font-size: 10px;
        line-height: 12px;
        /* Text/type0 */
        color: $text-type-0;
      }

      .list-items {
        height: 248px;
        overflow-y: scroll;
        scrollbar-width: thin;
        scrollbar-color: $purple;

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          /* Purple */
          background: $purple;
          border-radius: 22px;
        }

        .list-item {
          display: flex;
          align-items: center;
          margin-right: 9px;
          margin-bottom: 6px;
          background: -webkit-linear-gradient(
            90deg,
            rgba(156, 39, 176, 0.2) 100%,
            $bg-type-1 100%
          );
          background: -o-linear-gradient(
            90deg,
            rgba(156, 39, 176, 0.2) 100%,
            $bg-type-1 100%
          );
          background: -moz-linear-gradient(
            90deg,
            rgba(156, 39, 176, 0.2) 100%,
            $bg-type-1 100%
          );
          background: linear-gradient(
            90deg,
            rgba(156, 39, 176, 0.2) 100%,
            $bg-type-1 100%
          );
          border-radius: 5px;
          border: 2px solid $bg-type-1;
          padding: 4px 12px;

          &:last-child {
            margin-bottom: 0;
          }

          .source-icon {
            width: 20px;
            height: 20px;
            margin-right: 8px;
          }

          .source-name {
            /* 14/SemiBold */
            font-weight: 600;
            font-size: 14px;
            line-height: 22px;
            color: white;
            mix-blend-mode: normal;
            opacity: 0.9;

            &.clickable {
              &:hover {
                text-decoration: underline;
                cursor: pointer;
              }
            }
          }

          .fans-count {
            /* 12/Regular */
            font-size: 12px;
            line-height: 15px;
            color: white;
            mix-blend-mode: normal;
            opacity: 0.9;
            margin-left: auto;
          }
        }
      }
    }
  }

  .countries-card {
    .card-header {
      display: flex;
      justify-content: space-between;
    }
    .wrapper-of-buttons {
      display: flex;
      justify-content: space-between;
      background-color: $bg-type-4;
      border-radius: 8px;
      padding: 6px;
      margin-left: 15px;
    }
    .map-button {
      display: flex;
      gap: 8px;
      align-items: center;
      border-radius: 8px;
      background-color: $bg-type-4;

      padding: 8px;
      font-weight: bold;
      color: white;
    }
    .map-button.active {
      background-color: $bg-type-3;
    }

    .map,
    .countries-list {
      margin-top: 36px;
      height: 300px;
    }

    .countries-list {
      //overflow: hidden;
      .list-title {
        display: flex;
        justify-content: space-between;
        padding: 12px 8px;
        margin-right: 9px;
        /* 10/SemiBold */
        font-weight: 600;
        font-size: 10px;
        line-height: 12px;
        /* Text/type0 */
        color: $text-type-0;
      }

      .list-items {
        height: 248px;
        overflow-y: scroll;
        scrollbar-width: thin;
        scrollbar-color: $purple;

        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          /* Purple */
          background: $purple;
          border-radius: 22px;
        }

        .list-item {
          display: flex;
          align-items: center;
          margin-right: 9px;
          margin-bottom: 6px;
          /* BG/type1 */
          background: $bg-type-1;
          border-radius: 5px;
          padding: 4px 12px;

          &:last-child {
            margin-bottom: 0;
          }

          .country-flag {
            width: 20px;
            margin-right: 8px;
          }

          .country-name {
            /* 14/SemiBold */
            font-weight: 600;
            font-size: 14px;
            line-height: 22px;
            color: white;
            mix-blend-mode: normal;
            opacity: 0.9;

            &:hover {
              text-decoration: underline;
              cursor: pointer;
            }
          }

          .fans-count {
            /* 12/Regular */
            font-size: 12px;
            line-height: 15px;
            color: white;
            mix-blend-mode: normal;
            opacity: 0.9;
            margin-left: auto;
          }
        }
      }
    }
  }
}

.destination-links-card {
  .card-text {
    margin: 0;
    padding-bottom: 5px;
    font-size: 14px;
    line-height: 24px;
    /* Text/type0 */
    color: $text-type-0;
    mix-blend-mode: normal;
    opacity: 0.9;
  }

  .disabled-title {
    color: #ffffff;
    margin-top: 1.5rem;
  }

  table {
    width: 100%;
    border-collapse: collapse;

    thead {
      tr {
        th {
          padding: 18px 18px 10px 18px;
          /* 12/SemiBold */
          font-weight: 600;
          font-size: 12px;
          line-height: 15px;
          /* Text/type0 */
          color: $text-type-0;

          .th-content {
            display: flex;
            align-items: center;

            &.reorder {
              span {
                white-space: nowrap;
              }
            }
          }

          svg {
            margin-left: 5px;

            path {
              fill: $text-type-0;
            }
          }
        }
      }
    }

    tbody {
      tr {
        /* BG/type1 */
        background: $bg-type-1;
        border-bottom: 6px solid #24224a;

        .handle {
          border-radius: 10px 0 0 10px;
          padding-left: 18px;
          width: 24px;

          .handle-icon {
            display: flex;
            align-items: center;
            justify-content: center;

            &:hover {
              cursor: grab;

              svg {
                path {
                  fill: $purple;
                }
              }
            }
          }
        }

        .service {
          width: 600px;
          display: flex;
          align-items: center;

          .destination-logo {
            display: flex;
            justify-content: center;

            img {
              max-width: 135px;
              max-height: 51px;
            }
          }

          .platform {
            margin-left: 18px;
            font-weight: 600;
            font-size: 16px;
            line-height: 24px;
            color: #ffffff;
          }
        }

        .clickthroughs {
          border-radius: 0 10px 10px 0;
          font-weight: normal;
        }

        td {
          padding: 18px;
          border: solid 1px transparent;
          margin-bottom: 6px;
          /* 14/Regular */
          font-weight: 600;
          font-size: 14px;
          line-height: 22px;
          color: white;
          mix-blend-mode: normal;
          opacity: 0.9;

          svg {
            width: 24px;

            //margin-top: 3px;
            path {
              fill: white;
            }
          }

          .destination-image {
            height: 30px;
            width: auto;
          }
        }
      }
    }
  }
}

.background-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: unset;
  /* Purple-dark */
  background: $purple-dark;
  opacity: 0.9;
  backdrop-filter: blur(10px) !important;

  &.countries {
    z-index: 97;
  }

  &.calendar {
    // z-index: 99;
  }
}

.loading {
  display: flex;
  justify-content: center;

  svg {
    width: 100px;
    height: 100px;
    margin: 20px;
    display: inline-block;
  }
}

.delete-page {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  .button-secondary {
    margin: 0;
  }

  .button-text.danger {
    border: 1px #e3507a solid;
    border-radius: 5px;

    &:focus,
    &:hover {
      color: #ffffff;
      border-color: #ffffff;
    }
  }
}
.selected-range {
  color: #ffffff !important;
  border-radius: 25px;
}
.rangs {
  padding: 4px 7px 4px 0px;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: #c5c5ff;
  font-weight: 600;
  text-align: left;
  font-size: 14px;
}
.rangs:hover {
  color: white;
}
.allRangs {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-bottom: 28px;
}
.wrapper-of-rang-and-purple-circle {
  display: flex;
  align-items: center;
  padding-left: 24px;
  transition: padding-left 0.3s ease;
  position: relative;
}

.wrapper-of-rang-and-purple-circle.selected {
  padding-left: 0;
}

.purple-circle {
  width: 9px;
  height: 4.5px;
  border-radius: 100px 100px 0 0;
  background-color: $purple;
  margin-right: 8px;
  transform: rotate(90deg);
  transform-origin: center;
}
.purple-circle::after {
  content: "";
  position: absolute;
  top: -3px;
  left: -3px;
  width: 150%;
  height: 150%;
  border-radius: 100px 100px 0 0;
  background: radial-gradient(
    circle at top right,
    rgba(157, 83, 232, 0.01) 1%,
    rgba(157, 83, 232, 0.31) 31%,
    rgba(157, 83, 232, 0.01) 99%
  );
  box-shadow: 0 0 10px rgba(157, 83, 232, 0.5);
  z-index: -1;
}

@media (max-width: 1245px) {
  .content {
    padding: 36px 40px;
  }
  .split-cards {
    .sources-card {
      .card-title {
        display: block;
      }
      .select {
        float: inherit !important;
        margin-top: 0 !important;
      }
      .source-tab-category {
        flex-wrap: wrap;
      }
    }
  }
  .link-card {
    .link-details {
      flex-direction: column !important;
      .right {
        gap: 20px;
        flex-direction: row !important;
        margin-left: 18px;
        button {
          margin-top: 5px !important;
        }
      }
      .left {
        .link-url {
          margin-top: 0px;
        }
      }
    }
  }
}

@media (max-width: 1024px) {
  .breadcrumb {
    display: none;
  }
}
@media (max-width: 768px) {
  .content {
    padding: 36px 36px !important;
  }
  .destination-links-card {
    .table-container {
      table {
        th {
          padding: 18px 5px 10px 5px;
        }
        td {
          padding: 10px;
        }
        .handle {
          padding: 0;
        }
      }
    }
  }
}

@media (max-width: 660px) {
  .wrapper-of-rang-and-purple-circle {
    padding-left: 0;
  }
  .purple-circle {
    display: none;
  }
  .rangs {
    padding: 6px;
  }
  .metrics-container {
    justify-content: flex-start;
    margin-bottom: 24px;
  }
  .split-cards {
    grid-gap: 33px;
    // width: 97%;
  }
  // .content .split-cards{
  //   width: 97%;
  // }
  .allRangs {
    flex-direction: row;
    flex-wrap: wrap;
    padding-left: 25px;
    padding-top: 20px;
  }
  .none {
    display: none;
  }
  .loaderHeight {
    height: 100%;
  }
  .content {
    padding: 36px 36px !important;
    .l-container {
      height: 100%;
    }
    padding-left: 20px;
    padding-right: 20px;
    width: 100%;
    .split-cards {
      width: 100%;
      // padding-right: 7px;
      .l-container:first-of-type {
        flex-direction: row !important;
        flex: 0 0 100%;
        transition: transform 0.3s ease-in-out;
        height: 100%;
        // width: 100%;
      }
    }
    .link-card {
      flex-direction: column;
      .link-image {
        width: 100px;
        height: 100px;

        img {
          width: 100px;
          height: 100px;
        }
      }
      .link-details {
        flex-direction: column;
        .right {
          margin-bottom: 30px;
          flex-direction: column !important;
          margin-top: 18px;
          margin-left: 0;
          button {
            width: 100%;
            margin-top: 18px;
            // padding: 2px 10px;
          }
          .edit-page {
            width: 60px;
            height: 42px;
            border-radius: 10px;
            margin-top: 0px !important;
            background: none;
            // margin-bottom: 15px;
            padding: 0;
          }
        }
        .left {
          margin-left: 0;
          .left-title {
            margin-left: 120px;
            transform: translatey(-50%);
            margin-top: -50px;
            .link-title {
              font-size: 20px;
            }
          }
          .link-url {
            .url-box {
              padding-top: 8px;
              background: none;
              width: 100%;
              padding-left: 10px;
              padding-right: 10px;
              .copy {
                background: $gradient-type-2;
                border-radius: 5px;
                width: 48px;
                height: 34px;
                justify-content: center;
                margin-left: auto;

                svg {
                  margin-left: 0px;
                }
              }
              .external-link {
                background: $gradient-type-2;
                border-radius: 5px;
                width: 48px;
                height: 34px;
                justify-content: center;
                margin-left: 4px;
                svg {
                  margin-left: 0px;
                }
              }
              .url-text {
                color: white;
                width: calc(100% - 80px);
                overflow: hidden;
                width: 235px;
              }
            }
          }
        }
      }
    }
    .sources-card {
      margin-top: 0px;
      width: 40% !important;
    }

    .countries-card {
      width: 100%;
      margin-top: 0;
      // left: -10px;
    }
    .split-cards {
      display: flex;
      // flex-direction: column;
    }

    .destination-links-card {
      .table-container {
        overflow-x: auto;
      }
    }
  }

  .mobile-loaders-content {
    display: flex;
    height: 100%;
  }

  .l-container:first-of-type {
    width: 90vw;
    padding-right: 12px;
    // margin-right: 28px;
  }

  .link-card .link-image svg,
  .edit-page-span {
    display: none;
  }
  .chart-date-picker {
    width: 235px;
    margin-top: -72px;
    padding: 0px;
    height: 42px;
    .vue-daterange-picker {
      margin: 0px -20px;
      // width: 100%;
      min-width: auto;
      text-align: center;
      span {
        font-size: 10px;
      }
    }
    ::v-deep {
      .reportrange-text {
        padding: 11px 0px;
        height: 42px;
        width: 100%;
        background: $gradient-type-2;
        // margin-top: 35px;
        margin-bottom: 1px;
        margin-left: 20px;
      }
    }
  }
}

.destination-links-card {
  table {
    overflow-x: auto;
  }
}

.chart-card {
  height: 100%;
  margin-top: 0px;
  .card-top {
    .summary-stats {
      overflow: scroll;
      .state {
        width: 110px;
      }
      // .stat-box {
      //   border: 1px solid #9a2cca;
      // }
    }
  }
}

.delete-page {
  // padding-bottom: 20px;
}
.destination-links-card {
  padding: 12px;
}

.table-container thead {
  display: none;
}
.table-container tr {
  display: flex;
  flex-wrap: wrap;
  padding: 12px;
}

.table-container td {
  flex: 1 1 auto;
  position: relative;
}

.table-container td:nth-child(2)::before,
.table-container td:nth-child(3)::before,
.table-container td:nth-child(4)::before {
  content: attr(data-label);
  position: absolute;
  top: -22px;
  z-index: 1;
  font-size: 10px;
  color: $text-type-0;
}

.table-container td:nth-child(2)::before {
  content: "Direct Link";
  left: 0;
}
.table-container td:nth-child(3)::before {
  content: "Deeplink and Fallback";
  left: 0;
}
.table-container .itunes::before {
  content: "Clickthroughs" !important;
  left: 0;
}
.table-container td:nth-child(4)::before {
  content: "Clickthroughs";
  top: -7px;
  left: 0;
}

.table-container td:nth-child(2) button,
.table-container td:nth-child(3) button {
  padding: 4px 8px;
  flex: auto;
}

// .table-container td:nth-child(1) {
//   order: 1;
//   display: flex;
// }
.table-container td:nth-child(1) {
  width: 50%;
  order: 1;
}
.table-container td:nth-child(2) {
  order: 3;
  padding: 0px 12px 0px 0px !important;
  margin-bottom: 0px;
  width: 50%;
  display: flex;
  margin-top: 25px;
}
.table-container td:nth-child(3) {
  order: 4;
  width: 50%;
  padding: 0px !important;
  margin-bottom: 0px;
  display: flex;
  margin-top: 25px;
}
.table-container td:nth-child(4) {
  order: 2;
  width: 50%;
  // padding-left: 50% !important;
  margin-top: 7px;
}
.underline {
  text-decoration: underline;
}
.table-container-2 {
  table {
    border-collapse: separate;
    border-spacing: 0 7px;
    // table-layout: fixed;
    width: 100%;
    th {
      padding: 10px 0 !important;
    }
    .row {
      padding: 0 10px;
      .destination-details {
        // padding-top: 13px;
        display: flex;
        align-items: center;
        gap: 10px;
        height: 50px;
        padding-left: 10px;
        min-width: 200px;
        background: $bg-type-1;
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
        .destination-image {
          width: 24px;
          height: 24px;
          border-radius: 5px;
        }
        .destination-name {
          font-size: 14px;
          font-weight: 400;
          color: #ffffff;
        }
      }
      .clickthrough-data {
        height: 50px !important;
        min-width: 400px;
        font-size: 14px;
        color: #ffffff;
        background: $bg-type-1;
        padding-right: 20px;
        .clickthrough {
          display: flex;
          align-items: center;
          gap: 10px;
          height: 30px;
          padding: 0 10px;
          border-radius: 5px;
        }
      }
      .links {
        background: $bg-type-1;
        border-top-right-radius: 10px;
        border-bottom-right-radius: 10px;
        button {
          margin: 0;
          display: inline-block;
          span {
            font-size: 14px !important;
          }
          &.deeplink-button {
            margin-left: 10px;
          }
        }
      }
    }
  }
}
.calendarLoader {
  position: absolute;
  top: -7px;
  left: 0;
  right: 0;
  background: $bg-type-1;
}
.close-text {
  font-size: 6px;
  font-weight: 600;
  color: $text-type-2;
}
.close {
  display: block;
  display: flex;
  flex-direction: column;
  align-items: center;
  right: 24px;
  width: 24px;
  height: 24px;
  position: absolute;
  color: white;
  z-index: 200;
  top: 24px;
  svg {
    width: 16px;
    height: 16px;
    path {
      fill: $text-type-1;
    }
  }
}

@media (max-width: 530px) {
  .chart-date-picker {
    flex-wrap: wrap;
  }
}
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  .filter {
    width: fit-content;
    padding: 0 15px;
    height: 38px;
    font-size: 15px;
    color: #ffffff;
    background: #24224a;
    border-radius: 10px;
    display: flex;
    align-items: center;
    .category-name {
      font-weight: 500;
      margin-right: 10px;
    }
    .value {
      font-weight: 700;
      margin-right: 10px;
    }
    .remove {
      cursor: pointer;
      color: #ffffff;
      font-size: 18px;
      font-weight: 500;
    }
  }
}
.default-range-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  .filter {
    color: #c5c5ff;
    display: flex;
    align-items: center;
    font-weight: 500;
    padding: 5px 0;
    border-radius: 0;
    cursor: pointer;

    &.selected-range {
      color: #ffffff;
      border-bottom: 2px solid #9c27b0;
    }
  }
}
.closeNone {
  display: none;
}

.pixel-override-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pixel-override-modal {
  background: #24224a;
  border-radius: 10px;
  width: 620px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0px 50px 40px rgba(23, 18, 43, 0.3);

  .pixel-override-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 24px 0;

    .modal-title {
      font-weight: 600;
      font-size: 18px;
      line-height: 30px;
      color: white;
      opacity: 0.9;
    }

    .modal-close {
      background: none;
      border: none;
      color: white;
      font-size: 24px;
      cursor: pointer;
      padding: 0;
      line-height: 1;
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }
  }

  .pixel-override-body {
    padding: 24px;

    .pixels-table {
      margin-bottom: 24px;

      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0 6px;

        thead tr th {
          padding: 6px 18px 6px 0;
          font-weight: 600;
          font-size: 12px;
          color: $text-type-0;

          &:first-child {
            padding-left: 18px;
          }

          .th-content {
            display: flex;
            align-items: center;
          }
        }

        tbody tr {
          background: $bg-type-1;

          .provider {
            border-radius: 10px 0 0 10px;
            padding-left: 18px;

            span {
              font-size: 14px;
              line-height: 28px;
              color: white;
              opacity: 0.9;
            }
          }

          td {
            font-size: 14px;
            line-height: 28px;
            color: white;
            opacity: 0.9;
          }

          .actions .actions-container {
            display: flex;

            button {
              display: flex;
              align-items: center;
              background: none;
              border: none;
              margin-top: 0;
              cursor: pointer;

              span {
                color: $purple;
                font-weight: 600;
                font-size: 14px;
                line-height: 22px;
              }

              svg {
                width: 18px;
                margin-left: 8px;

                path {
                  fill: $purple;
                }
              }
            }
          }
        }
      }
    }

    .add-pixel {
      .add-pixel-title {
        font-weight: 600;
        font-size: 18px;
        line-height: 30px;
        color: white;
        opacity: 0.9;
        margin-bottom: 12px;
      }

      .providers {
        span {
          font-weight: 600;
          font-size: 14px;
          line-height: 17px;
          color: #59588d;
        }

        .remaining {
          margin: 6px 0 12px;
          display: flex;
          flex-wrap: wrap;

          .provider {
            margin-right: 6px;
            margin-bottom: 6px;
            padding: 12px 20px;
            border: 1px solid #b1afcd;
            border-radius: 8px;
            font-size: 16px;
            color: white;
            cursor: pointer;

            &.selected {
              border-color: #9c27b0;
            }
          }
        }

        .inputs {
          display: flex;
          gap: 10px;

          .social-input {
            flex: 1;

            label {
              margin-left: 11px;
              font-weight: bold;
              font-size: 12px;
              color: #8786ab;
            }

            .input-container {
              margin-top: 6px;
              display: flex;
              align-items: center;
              padding: 11px 13px;
              background: #100e2a;
              border: 1px solid #312f62;
              border-radius: 5px;

              input {
                flex: 1;
                background: #100e2a;
                outline: none;
                border: none;
                color: #b1afcd;
                font-size: 14px;

                &::placeholder {
                  color: #59588d;
                  font-size: 14px;
                }
              }
            }
          }
        }

        .pixel-actions {
          display: flex;
          justify-content: center;
          margin-top: 12px;

          .button-default {
            padding: 9px 30px;

            &:disabled {
              cursor: not-allowed;
              opacity: 0.5;
            }
          }
        }
      }
    }
  }
}
</style>
